import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { FormService } from './providers/form.service';
import { FormGroup } from './models/FormGroup';

import { distinctUntilChanged } from 'rxjs/operators';

import { ControlContainer } from './interfaces/ControlContainer';
import { ReactiveErrors } from './utils/reactive_errors';
import _ from 'lodash';

import { AbstractControl } from './models/AbstractControl';
import { FormControl } from './models/FormControl';
import { FormArray } from './models/FormArray';
import { isEmptyTemplate, untilDestroyed } from '@azora/common';


// import {ControlContentDirective} from './directives/control-content.directive';


export const formProvider: any = {
    provide: ControlContainer,
    useExisting: forwardRef(() => FormComponent)
};

@Component({
    selector: 'az-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [FormService, formProvider]
})

export class FormComponent extends ControlContainer implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    
    /**
     * @description
     * Tracks the `FormGroup` bound to this containers.
     */
    @Input('formGroup') form: FormGroup = null;
    
    @Input() mediaType: String;
    
    /**
     * @description
     * Emits an event when the form submission has been triggered.
     */
    @Output()
    submit = new EventEmitter();
    
    /**
     * @description
     * Emits an event when the form submission has been triggered and the form is invalid.
     */
    @Output()
    error = new EventEmitter();
    
    @ViewChild('contentVc', {read: ViewContainerRef, static: false})
    private _contentVcRef: ViewContainerRef;
    
    @ViewChild('controlTemplate', {static: false})
    controlTemplate: TemplateRef<any>;
    
    
    // @ContentChildren(ControlContentDirective)
    // controlContentDirList: QueryList<ControlContentDirective>;
    
    default_template: Boolean;
    
    controlNameTree
    
    
    constructor(
            private _formSvs: FormService,
            private _elRef: ElementRef,
            cd: ChangeDetectorRef
    ) {
        super(cd);
    }
    
    
    ngOnInit(): void {
        super.ngOnInit();
        this._checkFormPresent();
    }
    
    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        this._checkFormPresent();
        
        if (!changes.form.previousValue && changes.form.currentValue) {
            
            this.form.ngSubmit
                    .pipe(untilDestroyed(this), distinctUntilChanged())
                    .subscribe((data: { value: any, instant: boolean }) => {
                        if (this.form.invalid) {
                            this.error.emit(this.form.errors);
                        }
                        if (this.form.valid || data.instant) {
                            
                            this.submit.emit(this._formSvs.convertToMediaType(data.value, this.mediaType));
                        }
                        
                    });
            
        }
    }
    
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.default_template = isEmptyTemplate(this._elRef);
        this.controlNameTree = this._extractControlTree(this.form)
        this.cd.detectChanges();
    }
    
    ngOnDestroy(): void {
    }
    
    /**
     * @description
     * Returns the `FormGroup` bound to whole module.
     */
    get root(): FormGroup {
        return this.form;
    }
    
    
    /**
     * @description
     * Returns an array representing the path to this group. Because this containers
     * always lives at the top level of a form, it always an empty array.
     */
    get path(): string[] {
        return [];
    }
    
    private _checkFormPresent() {
        if (!this.form) {
            ReactiveErrors.missingFormException();
        }
    }
    
    private _extractControlTree = (control: AbstractControl) => {
        return _.map(control.controls, (value: AbstractControl, name) => {
            const controlType = this._extractControlType(value)
            
            if (controlType === 'FormControl') {
                return {name: name, type: controlType}
            } else {
                const controlChildren = this._extractControlTree(value)
                
                return {name: name, type: controlType, children: controlChildren}
            }
            
            
        })
    }
    
    private _extractControlType = (control: any) => {
        switch (true) {
            case (control instanceof FormControl) :
                return 'FormControl'
            case (control instanceof FormGroup) :
                
                return 'FormGroup'
            case (control instanceof FormArray) :
                return 'FormArray'
        }
    }
}
